import { scrubAnalyticsUrl } from "./constants";

/**
 * PostHog properties that hold the page URL or path. posthog-js fills them from `location`
 * on every event, in `$set_once`, and in the session props, so each can carry a sensitive
 * path's token or a `redirect` that leads to one.
 */
const POSTHOG_LOCATION_PROPERTIES = [
  "$current_url",
  "$referrer",
  "$session_entry_url",
  "$session_entry_referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$pathname",
  "$session_entry_pathname",
  "$initial_pathname",
] as const;

/**
 * PostHog `sanitize_properties` hook: runs {@link scrubAnalyticsUrl} over every location property.
 *
 * @param properties - The event (or `$set_once`) properties PostHog is about to send.
 * @returns The same object, with its location properties scrubbed.
 */
export function sanitizePostHogProperties<T extends Record<string, unknown>>(properties: T): T {
  const props: Record<string, unknown> = properties;
  for (const key of POSTHOG_LOCATION_PROPERTIES) {
    const value = props[key];
    if (typeof value === "string") props[key] = scrubAnalyticsUrl(value);
  }
  return properties;
}

/** Breadcrumb `data` keys that hold a URL (navigation `from`/`to`, fetch/xhr `url`). */
const SENTRY_BREADCRUMB_URL_KEYS = ["from", "to", "url"] as const;

/**
 * Sentry `beforeBreadcrumb` hook: scrubs the URLs a navigation or request breadcrumb records.
 *
 * @param breadcrumb - The breadcrumb Sentry is about to keep.
 * @returns The same breadcrumb, with its URL fields scrubbed.
 */
export function scrubSentryBreadcrumb<T extends { data?: Record<string, unknown> }>(breadcrumb: T): T {
  const data = breadcrumb.data;
  if (!data) return breadcrumb;
  for (const key of SENTRY_BREADCRUMB_URL_KEYS) {
    const value = data[key];
    if (typeof value === "string") data[key] = scrubAnalyticsUrl(value);
  }
  return breadcrumb;
}

/**
 * Sentry `beforeSend` hook: scrubs the request URL and query string, the transaction name, and any breadcrumbs
 * attached to the event.
 *
 * @param event - The event Sentry is about to send.
 * @returns The same event, scrubbed.
 */
export function scrubSentryEvent<
  T extends {
    request?: { url?: string; query_string?: unknown };
    transaction?: string;
    breadcrumbs?: { data?: Record<string, unknown> }[];
  },
>(event: T): T {
  if (event.request?.url) event.request.url = scrubAnalyticsUrl(event.request.url);
  // Sentry splits the query off `url`, and the sign-in hop carries the token in `?redirect=`.
  if (event.request && typeof event.request.query_string === "string") {
    event.request.query_string = scrubAnalyticsUrl(`/?${event.request.query_string}`).replace(/^\/\??/, "");
  }
  if (event.transaction) event.transaction = scrubAnalyticsUrl(event.transaction);
  event.breadcrumbs?.forEach(scrubSentryBreadcrumb);
  return event;
}
